import React, { useState, useEffect, useRef } from "react"

export default function MoveNodeModal({
  isOpen,
  onClose,
  categories,
  onSelect,
  excludeIds,
  selectedCount,
  selectedNames,
}) {
  const [search, setSearch] = useState("")
  const [highlightIdx, setHighlightIdx] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 50)
  }, [isOpen])

  useEffect(() => { setHighlightIdx(0) }, [search])

  useEffect(() => {
    const onKey = (e) => {
      if (!isOpen) return
      const filtered = categories.filter(
        (c) =>
          !excludeIds.includes(c.id) &&
          (c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.slug.toLowerCase().includes(search.toLowerCase()))
      )
      const max = Math.min(filtered.length - 1, 14)
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowDown") { e.preventDefault(); setHighlightIdx((p) => Math.min(p + 1, max)) }
      else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightIdx((p) => Math.max(p - 1, 0)) }
      else if (e.key === "Enter" && filtered[highlightIdx]) {
        e.preventDefault()
        onSelect(filtered[highlightIdx].id)
        onClose()
        setSearch("")
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen, onClose, search, highlightIdx, categories, excludeIds, onSelect])

  useEffect(() => {
    listRef.current?.children[highlightIdx]?.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }, [highlightIdx])

  if (!isOpen) return null

  const filtered = categories.filter(
    (c) =>
      !excludeIds.includes(c.id) &&
      (c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.slug.toLowerCase().includes(search.toLowerCase()))
  )

  const displayNames =
    selectedNames.length <= 2
      ? selectedNames.join(", ")
      : `${selectedNames.slice(0, 2).join(", ")} +${selectedNames.length - 2}`

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-[440px] max-h-[70vh] bg-admin_paper rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-admin_bordered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-admin_bordered">
          <h3 className="text-admin_text font-semibold text-sm">
            Move{" "}
            {selectedCount > 1 ? `${selectedCount} nodes` : `"${displayNames}"`}
          </h3>
          <button
            onClick={onClose}
            className="text-admin_text_dim hover:text-admin_text transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        {selectedCount > 1 && (
          <div className="px-4 py-2 border-b border-admin_bordered flex flex-wrap gap-1.5">
            {selectedNames.map((name, i) => (
              <span
                key={i}
                className="px-2 py-0.5 bg-admin_primary/20 text-admin_primary text-xs rounded-full"
              >
                {name}
              </span>
            ))}
          </div>
        )}

        <div className="px-4 pt-3 pb-2">
          <input
            ref={inputRef}
            type="text"
            placeholder="Search destination..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-admin_canvas border border-admin_bordered rounded-lg px-3 py-2 text-admin_text text-sm focus:outline-none focus:border-admin_primary"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5" ref={listRef}>
          {filtered.length === 0 && (
            <p className="text-center text-admin_text_dim text-sm py-6">No nodes found</p>
          )}
          {filtered.slice(0, 15).map((cat, idx) => (
            <button
              key={cat.id}
              onClick={() => { onSelect(cat.id); onClose(); setSearch("") }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                idx === highlightIdx ? "bg-admin_primary/15" : "hover:bg-admin_canvas"
              }`}
            >
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: cat.color }}
              />
              <span className="text-admin_text text-sm">{cat.name}</span>
              <span className="text-admin_text_dim text-xs ml-auto">{cat.slug}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
